/**
 * AI assist (TMMAPP-0013, TMMAPI-0018) — category suggestions, envelope analysis, match-proposal
 * accept / reject. 0011's `aiMatch.ts` lives here so the review queue keeps working.
 */
import { normalizeAiJobFailureReason, type AiJobFailureReason } from './aiJobFailure';
import { request } from './client';
import { normalizeReviewItem, type ReviewItem } from './review';
import type {
  CategorySuggestion as WireCategorySuggestion,
  CategorySuggestionLineRequest,
  CategorySuggestionRequest,
  MatchSuggestionResponse,
  ReviewItem as WireReviewItem,
  CategorySuggestionResponse as WireSuggestions,
  EnvelopeAnalysisCreateRequest,
  EnvelopeAnalysisListResponse as WireList,
  EnvelopeAnalysisFinding as WireFinding,
  EnvelopeAnalysisResponse as WireAnalysis,
} from './generated';

export interface CategorySuggestionLine {
  allocationId?: number;
  payee: string;
  amountMinor: number;
  sign: number;
}

/** TMMAPI-0157's provenance on a category suggestion (TMM-B9): payee memory or a model. */
export type CategorySuggestionSource = NonNullable<WireCategorySuggestion['source']>;

export interface CategorySuggestion {
  lineIndex: number;
  allocationId: number | null;
  categoryId: number | null;
  confidence: number;
  reason: string | null;
  /** Kept only when the wire serves it, never inferred: the endpoint mixes memory and AI results. */
  source: CategorySuggestionSource | null;
}

export type AnalysisStatus = 'PENDING' | 'READY' | 'FAILED';
export type AnalysisFindingKind = 'VARIANCE' | 'TREND' | 'ANOMALY' | 'PACE' | 'RUNWAY';

export interface EnvelopeAnalysisFinding {
  kind: AnalysisFindingKind | null;
  text: string;
  month: string | null;
  amountMinor: number | null;
  categoryId: number | null;
}

export interface EnvelopeAnalysis {
  id: number;
  status: AnalysisStatus;
  body: string | null;
  from: string;
  to: string;
  periodKind: string;
  createdAt: string;
  findings: EnvelopeAnalysisFinding[];
  envelopeId: number;
  /** Why a FAILED analysis failed (TMMAPI-0152); null otherwise and on older ones. */
  failureReason: AiJobFailureReason | null;
  /** Served only to the person whose wallet was refused, while their first purchase is due. */
  firstPurchaseRequired: boolean;
  /**
   * The wallet's debt, only when `failureReason` is `AI_DEBT` and only to the owner (TMMAPI-0162
   * U6). Null when not served.
   */
  debtMinor: number | null;
}

const STATUSES: ReadonlySet<string> = new Set(['PENDING', 'READY', 'FAILED']);
const KINDS: ReadonlySet<string> = new Set(['VARIANCE', 'TREND', 'ANOMALY', 'PACE', 'RUNWAY']);
const SOURCES: ReadonlySet<string> = new Set(['MEMORY', 'AI']);

function normalizeFinding(w: WireFinding): EnvelopeAnalysisFinding {
  return {
    kind: KINDS.has(w.kind) ? w.kind : null,
    text: w.text,
    month: w.month ?? null,
    amountMinor: w.amountMinor ?? null,
    categoryId: w.categoryId ?? null,
  };
}

export function normalizeAnalysis(w: WireAnalysis): EnvelopeAnalysis {
  return {
    id: w.id,
    status: STATUSES.has(w.status) ? w.status : 'PENDING',
    body: w.body ?? null,
    from: w.from,
    to: w.to,
    periodKind: w.periodKind,
    createdAt: w.createdAt,
    findings: (w.findings ?? []).map(normalizeFinding),
    envelopeId: w.envelopeId,
    failureReason: normalizeAiJobFailureReason(w.failureReason),
    firstPurchaseRequired: w.firstPurchaseRequired ?? false,
    debtMinor: w.debtMinor ?? null,
  };
}

function normalizeSuggestion(w: WireCategorySuggestion): CategorySuggestion {
  return {
    lineIndex: w.lineIndex,
    allocationId: w.allocationId ?? null,
    categoryId: w.categoryId ?? null,
    confidence: w.confidence,
    reason: w.reason ?? null,
    source: SOURCES.has(w.source) ? w.source : null,
  };
}

export const aiApi = {
  /** POST /v1/virtual-accounts/{id}/category-suggestions — categories belong to the virtual account (TMMAPI-0145). */
  async categorySuggestions(
    virtualAccountId: number,
    lines: CategorySuggestionLine[],
  ): Promise<CategorySuggestion[]> {
    const body: CategorySuggestionRequest = {
      lines: lines.map((line) => {
        const row: CategorySuggestionLineRequest = {
          payee: line.payee,
          amountMinor: line.amountMinor,
          sign: line.sign,
        };
        if (line.allocationId !== undefined) row.allocationId = line.allocationId;
        return row;
      }),
    };
    const wire = await request<WireSuggestions>(
      'POST',
      `/v1/virtual-accounts/${virtualAccountId}/category-suggestions`,
      { json: body },
    );
    return wire.suggestions.map(normalizeSuggestion);
  },

  async requestAnalysis(
    envelopeId: number,
    body: { from?: string; to?: string } = {},
  ): Promise<EnvelopeAnalysis> {
    const json: EnvelopeAnalysisCreateRequest = {};
    if (body.from !== undefined) json.from = body.from;
    if (body.to !== undefined) json.to = body.to;
    return normalizeAnalysis(
      await request<WireAnalysis>('POST', `/v1/envelopes/${envelopeId}/analyses`, { json }),
    );
  },

  async getAnalysis(envelopeId: number, id: number): Promise<EnvelopeAnalysis> {
    return normalizeAnalysis(
      await request<WireAnalysis>('GET', `/v1/envelopes/${envelopeId}/analyses/${id}`),
    );
  },

  async listAnalyses(envelopeId: number): Promise<EnvelopeAnalysis[]> {
    const wire = await request<WireList>('GET', `/v1/envelopes/${envelopeId}/analyses`);
    return wire.items.map(normalizeAnalysis);
  },

  async acceptMatchProposal(
    accountId: number,
    proposalId: number,
    idempotencyKey: string,
  ): Promise<void> {
    await request<void>(
      'POST',
      `/v1/me/accounts/${accountId}/match-proposals/${proposalId}/accept`,
      { idempotencyKey },
    );
  },

  async rejectMatchProposal(
    accountId: number,
    proposalId: number,
    idempotencyKey: string,
  ): Promise<void> {
    await request<void>(
      'POST',
      `/v1/me/accounts/${accountId}/match-proposals/${proposalId}/reject`,
      { idempotencyKey },
    );
  },
};

/** Drop-in for 0011's review queue (`src/api/aiMatch.ts`). */
export const aiMatchApi = {
  async suggestMatches(accountId: number): Promise<MatchProposal[]> {
    const wire = await request<MatchSuggestionResponse>(
      'POST',
      `/v1/me/accounts/${accountId}/match-suggestions`,
    );
    return wire.proposals.flatMap((row) => {
      // A match suggestion is model-produced, so the review shape carries source AI.
      const item: WireReviewItem = {
        kind: 'AI_MATCH_PROPOSAL',
        source: 'AI',
        accountId,
        proposalId: row.id,
        seriesId: row.seriesId,
        transactionId: row.transactionId,
        originalOn: row.originalOn,
        confidence: row.confidence,
      };
      if (row.reason !== undefined) item.reason = row.reason;
      const proposal = normalizeReviewItem(item);
      return proposal ? [proposal] : [];
    });
  },
  accept: aiApi.acceptMatchProposal,
  reject: aiApi.rejectMatchProposal,
};

export type MatchProposal = ReviewItem;
